import React, { useState, useEffect, useMemo } from "react";
import { useStorePacksContext } from "./StorePacks.jsx";
import { useSkinsMasterContext } from "./SkinsMaster.jsx";
import { useAuthContext } from "@/wrappers/AuthWrapper.jsx";
import { gets_skinurl, rarity_colors, get_primary_color, get_accent_color } from "./dependencies.jsx";
import { TokenIcon } from "../../components/utilityComps.jsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SkinStorePurchaseModal } from "./SkinStorePurchaseModal.jsx";
import { Skin3D_Preview } from "./Skin3D_Preview.jsx";

export const StorePackDetails = () => {
  const store_con = useStorePacksContext();
  const skm_con = useSkinsMasterContext();
  const aucon = useAuthContext();
  const [view_mode, set_view_mode] = useState("image"); // "image" or "3d"
  const [current_skin_index, set_current_skin_index] = useState(0);

  const { selected_pack, set_selected_pack, store_indis_map } = store_con;
  const { _bal, isauvault } = skm_con;

  // Get pack data (with fallbacks for when selected_pack is null)
  const { id, name, price_usd, price_sgc, skinindi_ids } = selected_pack || {};

  // Get pack contents with skin data
  const pack_skins = useMemo(() => {
    if (!skinindi_ids || !store_indis_map) return [];
    return skinindi_ids.map(skin_id => {
      const skin_data = store_indis_map[skin_id];
      return {
        id: skin_id,
        ...skin_data
      };
    }).filter(Boolean);
  }, [skinindi_ids, store_indis_map]);

  const current_skin = pack_skins[current_skin_index];
  const skin_image_url = current_skin ? gets_skinurl(current_skin.name, current_skin.rvmode, true) : null;

  const primary_color = current_skin ? get_primary_color(current_skin.name) : "#4A5568";
  const accent_color = current_skin ? get_accent_color(current_skin.name) : "#718096";

  // Auto-cycle in image mode every 3 seconds
  useEffect(() => {
    if (view_mode === "image" && pack_skins.length > 1) {
      const interval = setInterval(() => {
        set_current_skin_index(prev => (prev + 1) % pack_skins.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [view_mode, pack_skins.length]);

  // Early return after all hooks
  if (!selected_pack) return null;

  const handle_back = () => {
    set_selected_pack(null);
  };

  // Post-purchase refresh function
  const handle_post_purchase = async () => {
    try {
      // Refresh owned skins, store data, and balances after purchase
      if (skm_con.post_purchase_function) {
        await skm_con.post_purchase_function();
      }
    } catch (error) {
      console.error("Failed to refresh data after purchase:", error);
    }
  };

  const handle_prev_skin = () => {
    set_current_skin_index(prev =>
      prev === 0 ? pack_skins.length - 1 : prev - 1
    );
  };

  const handle_next_skin = () => {
    set_current_skin_index(prev => (prev + 1) % pack_skins.length);
  };

  return (
    <div className="w-full h-full">
      {/* Header Row - Back Button */}
      <div className="flex items-center mb-4 sm:mb-6">
        <button
          onClick={handle_back}
          className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-slate-600/40 hover:bg-slate-500/50 rounded-lg text-white transition-all duration-200 backdrop-blur-md border border-slate-500/30"
        >
          <svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="text-sm sm:text-base font-medium">Back to Packs</span>
        </button>
      </div>

      {/* Full Width Info Row */}
      <div
        className="flex flex-col mb-6 sm:mb-8 p-4 sm:p-6 rounded-xl backdrop-blur-md border border-slate-500/30"
        style={{
          background: `linear-gradient(135deg, ${primary_color}08, ${primary_color}04)`,
        }}
      >
        {/* Top Row: Pack Name and Price */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
          {/* Left: Pack Name and Pack Badge */}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white font-digi">
                {name} Pack
              </h1>
              <div className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-bold text-white bg-purple-600">
                PACK
              </div>
            </div>
          </div>

          {/* Right: Price Display */}
          <div className="text-left md:text-right">
            <div className="flex flex-row md:flex-col gap-3 md:gap-2">
              {/* USD Price */}
              <div className="flex items-center justify-start md:justify-end space-x-1.5 sm:space-x-2">
                <div className="flex -space-x-1">
                  <TokenIcon token="WETH" size="md" className="z-10" />
                  <TokenIcon token="DEZ" size="md" className="z-0" />
                </div>
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                  ${price_usd}
                </span>
              </div>

              {/* OR Divider */}
              <div className="text-center text-gray-400 text-xs sm:text-sm self-center">or</div>

              {/* SGC Price */}
              <div className="flex items-center justify-start md:justify-end space-x-1.5 sm:space-x-2">
                <TokenIcon token="SGC" size="md" />
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-r3reg">
                  {price_sgc} SGC
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Pack Contents List */}
        <div>
          <h3 className="text-sm sm:text-base md:text-lg font-medium text-gray-300 mb-2 sm:mb-3">
            Pack includes {pack_skins.length} skins:
          </h3>
          <div className="flex flex-wrap gap-2 sm:gap-3 max-h-[150px] overflow-y-auto">
            {pack_skins.map((skin, index) => {
              const rarity_color = rarity_colors[skin.rarity] || rarity_colors.common;
              return (
                <div
                  key={skin.id}
                  className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg backdrop-blur-md border ${
                    index === current_skin_index
                      ? 'border-white/50 bg-white/10'
                      : 'border-slate-500/30 bg-slate-600/20'
                  }`}
                >
                  <div
                    className="w-2 h-2 sm:w-3 sm:h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: rarity_color }}
                  />
                  <span className="text-white text-xs sm:text-sm font-medium truncate">
                    {skin.name}
                  </span>
                  <span className="text-gray-400 text-[10px] sm:text-xs flex-shrink-0">
                    ({skin.rvmode})
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 50/50 Split Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:h-[600px]">
        {/* Left Panel - Purchase Options */}
        <div className="space-y-4 sm:space-y-6 max-h-[500px] lg:max-h-none overflow-y-auto">
          {/* Purchase Options Card */}
          <div className="bg-slate-600/20 border border-slate-500/30 rounded-lg p-4 sm:p-6 backdrop-blur-md">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 font-digi text-center">
              Purchase Options
            </h3>

            <div className="space-y-3 sm:space-y-4">
              {/* SGC Option */}
              <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
                <div className="flex items-center space-x-3">
                  <TokenIcon token="SGC" size="md" />
                  <span className="text-white font-medium">SGC</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-r3reg">{price_sgc} SGC</div>
                </div>
              </div>

              {/* DEZ Option */}
              <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
                <div className="flex items-center space-x-3">
                  <TokenIcon token="DEZ" size="md" />
                  <span className="text-white font-medium">DEZ</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-white">${price_usd}</div>
                  <div className="text-sm text-gray-400">
                    {skm_con.usd_to_tok_val(price_usd, "DEZ")?.toFixed(2)} DEZ
                  </div>
                </div>
              </div>

              {/* WETH Option */}
              <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600/50">
                <div className="flex items-center space-x-3">
                  <TokenIcon token="WETH" size="md" />
                  <span className="text-white font-medium">WETH</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-white">${price_usd}</div>
                  <div className="text-sm text-gray-400">
                    {skm_con.usd_to_tok_val(price_usd, "WETH")?.toFixed(4)} WETH
                  </div>
                </div>
              </div>
            </div>

            {/* Buy Pack Now Button */}
            <div className="mt-6 text-center">
              {isauvault ? (
                <SkinStorePurchaseModal
                  item={selected_pack}
                  item_type="pack"
                  post_buy_function={handle_post_purchase}
                  trigger={
                    <button className="w-full py-4 px-6 rounded-lg font-bold text-lg transition-all duration-200 bg-gradient-to-r from-r3reg to-r3dark hover:from-r3dark hover:to-r3reg text-white shadow-lg hover:shadow-xl transform hover:scale-105">
                      Buy Pack Now
                    </button>
                  }
                />
              ) : (
                <button
                  onClick={() => aucon.open_loginpop()}
                  className="w-full py-4 px-6 rounded-lg font-bold text-lg transition-all duration-200 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Login to Buy Pack
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Cycling Preview */}
        <div className="space-y-4 sm:space-y-6">
          {/* Preview Panel with Mode Toggle */}
          <div className="rounded-xl overflow-hidden backdrop-blur-md border border-slate-500/30">
            {/* Header with View Mode Toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 border-b border-slate-500/30">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white font-digi">
                Pack Preview {current_skin ? `(${current_skin_index + 1}/${pack_skins.length})` : ''}
              </h3>

              {/* View Mode Button Group */}
              <div className="flex bg-slate-700/40 rounded-lg p-1">
                <button
                  onClick={() => set_view_mode("image")}
                  className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 ${
                    view_mode === "image"
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-gray-300 hover:text-white hover:bg-slate-600/50"
                  }`}
                >
                  {/* Image Icon */}
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">Image</span>
                </button>

                <button
                  onClick={() => set_view_mode("3d")}
                  className={`flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md transition-all duration-200 ${
                    view_mode === "3d"
                      ? "bg-purple-600 text-white shadow-md"
                      : "text-gray-300 hover:text-white hover:bg-slate-600/50"
                  }`}
                >
                  {/* 3D Cube Icon */}
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium">3D</span>
                </button>
              </div>
            </div>

            {/* Content Area with Navigation */}
            <div className="p-3 sm:p-4 space-y-3">
              {/* Preview Area with Navigation Buttons */}
              <div className="aspect-[1166/656] relative">
                {view_mode === "image" ? (
                  // Image View with Cycling
                  <>
                    {skin_image_url ? (
                      <img
                        src={skin_image_url}
                        alt={current_skin?.name}
                        className="w-full h-full object-cover rounded-lg shadow-lg"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-600/60 to-slate-800/80 flex items-center justify-center rounded-lg">
                        <span className="text-gray-400 text-lg font-medium">
                          No Image Available
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  // 3D Model View
                  <Skin3D_Preview
                    skin_name={current_skin?.name}
                    rvmode={current_skin?.rvmode}
                  />
                )}

                {/* Navigation Chevrons (on both views) */}
                {pack_skins.length > 1 && (
                  <>
                    <button
                      onClick={handle_prev_skin}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-200 z-10"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={handle_next_skin}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all duration-200 z-10"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
              </div>

              {/* Current Skin Info Below */}
              {current_skin && (
                <div className="bg-black/60 backdrop-blur-md rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{current_skin.name}</div>
                      <div className="text-gray-300 text-sm">{current_skin.rvmode} • {current_skin.rarity}</div>
                    </div>
                    <div className="flex space-x-1">
                      {pack_skins.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full ${
                            index === current_skin_index ? 'bg-white' : 'bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};